<template>
  <div class="publish-settings">
    <div>
      <div class="header" style="text-align: center;">
        <img v-if="currentLogoUrl" :src="currentLogoUrl" alt="Logo" class="logo" />
        <div v-else class="logo-placeholder"></div>
      </div>

      <el-form label-width="120px" class="form">
        <h5 style="text-align: center;">系统发布设置</h5>

        <el-form-item label="学校标识">
          <div class="action-row">
            <el-upload
              ref="logoUploadRef"
              action=""
              accept="image/*"
              :auto-upload="false"
              :limit="1"
              :file-list="logoFileList"
              :on-change="onLogoChange"
              :on-remove="onLogoRemove"
              :show-file-list="true"
            >
              <el-button type="primary">选择标识</el-button>
            </el-upload>
            <el-button type="success" :loading="logoUploading" @click="uploadLogo">上传标识</el-button>
          </div>
        </el-form-item>

        <el-form-item label="校园平面图">
          <div class="upload-area">
            <div class="action-row">
              <el-upload
                ref="planUploadRef"
                action=""
                accept="image/*"
                multiple
                :auto-upload="false"
                :file-list="planFileList"
                :on-change="onPlanChange"
                :on-remove="onPlanRemove"
                :show-file-list="true"
              >
                <el-button type="primary">选择平面图</el-button>
              </el-upload>
              <el-button type="success" :loading="plansUploading" @click="uploadCampusPlans">导入平面图</el-button>
            </div>
          </div>
        </el-form-item>

        <el-form-item label="已上传平面图">
          <div v-if="campusPlans.length" class="plan-grid">
            <div v-for="plan in campusPlans" :key="plan.name" class="plan-item">
              <el-image
                class="plan-image"
                :src="plan.url"
                fit="cover"
                :preview-src-list="planPreviewList"
                :initial-index="planPreviewList.indexOf(plan.url)"
                preview-teleported
              />
              <div class="plan-name">{{ plan.displayName }}</div>
            </div>
          </div>
          <el-empty v-else description="暂无校园平面图" :image-size="80" />
        </el-form-item>

        <el-form-item label="查询开放时间">
          <div class="action-row time-row">
            <el-date-picker
              v-model="queryOpenTime"
              type="datetime"
              placeholder="选择开放时间"
              format="YYYY-MM-DD HH:mm:ss"
              value-format="YYYY-MM-DD HH:mm:ss"
              clearable
            />
            <el-button type="primary" :loading="queryTimeSaving" @click="saveQueryOpenTime">保存时间</el-button>
          </div>
        </el-form-item>

        <el-form-item label="二维码姓名水印">
          <div class="action-row">
            <el-switch
              v-model="qrWatermarkEnabled"
              active-text="启用"
              inactive-text="关闭"
            />
            <el-button type="primary" :loading="qrWatermarkSaving" @click="saveQrWatermarkSetting">
              保存设置
            </el-button>
          </div>
        </el-form-item>

        <div class="hint">当前状态：{{ queryOpenStateText }}</div>
      </el-form>

      <div class="copyright">
        <p>© 2024-2026 起航工作室版权所有</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus';
import axios from 'axios';

export default {
  name: 'PublishSettings',
  data() {
    return {
      currentLogoUrl: '',
      selectedLogo: null,
      logoFileList: [],
      planFileList: [],
      campusPlans: [],
      queryOpenTime: '',
      queryIsOpen: true,
      qrWatermarkEnabled: false,
      logoUploading: false,
      plansUploading: false,
      queryTimeSaving: false,
      qrWatermarkSaving: false,
    };
  },
  computed: {
    planPreviewList() {
      return this.campusPlans.map((plan) => plan.url);
    },
    queryOpenStateText() {
      if (!this.queryOpenTime) {
        return '未设置，查询立即开放';
      }
      return this.queryIsOpen ? `已开放（开放时间：${this.queryOpenTime}）` : `未开放（开放时间：${this.queryOpenTime}）`;
    },
  },
  mounted() {
    this.fetchLogo();
    this.fetchCampusPlans();
    this.fetchQueryOpenTime();
    this.fetchQrWatermarkSetting();
  },
  methods: {
    onLogoChange(file, fileList) {
      this.selectedLogo = file.raw;
      this.logoFileList = fileList.slice(-1);
    },
    onLogoRemove() {
      this.selectedLogo = null;
      this.logoFileList = [];
    },
    onPlanChange(file, fileList) {
      this.planFileList = fileList;
    },
    onPlanRemove(file, fileList) {
      this.planFileList = fileList;
    },
    async fetchLogo() {
      try {
        const response = await axios.get('/api/logo', { responseType: 'blob' });
        if (this.currentLogoUrl) {
          URL.revokeObjectURL(this.currentLogoUrl);
        }
        this.currentLogoUrl = URL.createObjectURL(response.data);
      } catch (error) {
        console.error('Error fetching logo:', error);
      }
    },
    async uploadLogo() {
      if (!this.selectedLogo) {
        ElMessage.error('请先选择学校标识');
        return;
      }

      this.logoUploading = true;
      const formData = new FormData();
      formData.append('logo', this.selectedLogo);

      try {
        await axios.post('/upload/logo', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        ElMessage.success('学校标识上传成功');
        this.selectedLogo = null;
        this.logoFileList = [];
        this.$refs.logoUploadRef.clearFiles();
        this.fetchLogo();
      } catch (error) {
        console.error('Error uploading logo:', error);
        ElMessage.error('学校标识上传失败');
      } finally {
        this.logoUploading = false;
      }
    },
    async fetchCampusPlans() {
      try {
        const response = await axios.get('/api/campus-plans');
        this.campusPlans = response.data.plans || [];
      } catch (error) {
        console.error('Error fetching campus plans:', error);
        ElMessage.error('无法获取校园平面图');
      }
    },
    async uploadCampusPlans() {
      const selectedPlans = this.planFileList.map((file) => file.raw).filter(Boolean);
      if (!selectedPlans.length) {
        ElMessage.error('请先选择校园平面图');
        return;
      }

      this.plansUploading = true;
      const formData = new FormData();
      selectedPlans.forEach((file) => {
        formData.append('plans', file);
      });

      try {
        await axios.post('/api/campus-plans', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        ElMessage.success('校园平面图导入成功');
        this.planFileList = [];
        this.$refs.planUploadRef.clearFiles();
        this.fetchCampusPlans();
      } catch (error) {
        console.error('Error uploading campus plans:', error);
        ElMessage.error('校园平面图导入失败');
      } finally {
        this.plansUploading = false;
      }
    },
    async fetchQueryOpenTime() {
      try {
        const response = await axios.get('/api/query-open-time');
        this.queryOpenTime = response.data.queryTime || '';
        this.queryIsOpen = response.data.isOpen !== false;
      } catch (error) {
        console.error('Error fetching query open time:', error);
        ElMessage.error('无法获取查询开放时间');
      }
    },
    async saveQueryOpenTime() {
      this.queryTimeSaving = true;
      try {
        const response = await axios.post('/api/query-open-time', {
          queryTime: this.queryOpenTime || '',
        });
        this.queryOpenTime = response.data.queryTime || '';
        this.queryIsOpen = response.data.isOpen !== false;
        ElMessage.success('查询开放时间保存成功');
      } catch (error) {
        console.error('Error saving query open time:', error);
        ElMessage.error('查询开放时间保存失败');
      } finally {
        this.queryTimeSaving = false;
      }
    },
    async fetchQrWatermarkSetting() {
      try {
        const response = await axios.get('/api/qr-watermark-setting');
        this.qrWatermarkEnabled = response.data.enabled === true;
      } catch (error) {
        console.error('Error fetching QR watermark setting:', error);
        ElMessage.error('无法获取二维码水印设置');
      }
    },
    async saveQrWatermarkSetting() {
      this.qrWatermarkSaving = true;
      try {
        const response = await axios.post('/api/qr-watermark-setting', {
          enabled: this.qrWatermarkEnabled,
        });
        this.qrWatermarkEnabled = response.data.enabled === true;
        ElMessage.success('二维码水印设置保存成功');
      } catch (error) {
        console.error('Error saving QR watermark setting:', error);
        ElMessage.error('二维码水印设置保存失败');
      } finally {
        this.qrWatermarkSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.publish-settings {
  padding: 20px;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.header {
  display: grid;
  place-items: center;
  width: 100%;
  height: auto;
  margin-bottom: 5px;
}

.logo {
  width: 72px;
  height: auto;
  display: block;
}

.logo-placeholder {
  width: 72px;
  height: 72px;
  margin-bottom: 20px;
  background-color: #f5f5f5;
  border-radius: 4px;
}

.form {
  margin-top: 5px;
  flex: 1;
}

.action-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
}

.upload-area {
  width: 100%;
}

.time-row {
  align-items: center;
}

.plan-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
  width: 100%;
}

.plan-item {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
}

.plan-image {
  display: block;
  width: 100%;
  height: 120px;
}

.plan-name {
  padding: 8px;
  font-size: 12px;
  color: #606266;
  line-height: 1.3;
  word-break: break-all;
}

.hint {
  color: #606266;
  margin-top: 10px;
  text-align: center;
}

.copyright {
  text-align: center;
  margin-top: 30px;
  padding: 15px;
  font-size: 12px;
  color: #666;
}
</style>
